import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem
} from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  UserCheck, Search, RefreshCw, AlertCircle, CheckCircle2,
  ChevronUp, ChevronDown
} from 'lucide-react';

/**
 * v5.4.12 — Owner Assignment Page
 * - View all equipment grouped by current owner
 * - Filter by "without owner"
 * - Multi-select and bulk-assign a creator (cascades to all child units)
 * - Override toggle for already-owned items
 */
export default function OwnerAssignmentPage() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('without_owner'); // 'without_owner' | 'all' | 'with_owner'
  const [selected, setSelected] = useState(new Set());
  const [creatorId, setCreatorId] = useState('');
  const [notifyEmail, setNotifyEmail] = useState('');
  const [override, setOverride] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sortBy, setSortBy] = useState('name'); // 'name' | 'units' | 'owner'
  const [sortDir, setSortDir] = useState('asc');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api().get('/admin/owner-stats');
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo cargar la lista de equipos');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    if (!data) return [];
    let list = [...data.equipment];

    if (filter === 'without_owner') list = list.filter((e) => !e.owner_id);
    if (filter === 'with_owner') list = list.filter((e) => !!e.owner_id);

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((e) =>
        e.equipment_name.toLowerCase().includes(q) ||
        (e.owner_name || '').toLowerCase().includes(q) ||
        (e.owner_username || '').toLowerCase().includes(q)
      );
    }

    const dir = sortDir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      let av; let bv;
      if (sortBy === 'name')  { av = a.equipment_name.toLowerCase(); bv = b.equipment_name.toLowerCase(); }
      else if (sortBy === 'units') { av = a.unit_count; bv = b.unit_count; }
      else { av = (a.owner_name || '').toLowerCase(); bv = (b.owner_name || '').toLowerCase(); }
      if (av < bv) return -1 * dir;
      if (av > bv) return  1 * dir;
      return 0;
    });

    return list;
  }, [data, filter, search, sortBy, sortDir]);

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.equipment_id)));
  };

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
  };

  const apply = async () => {
    if (!creatorId) { toast.error('Selecciona un usuario'); return; }
    if (selected.size === 0) { toast.error('Selecciona al menos un equipo'); return; }
    const creatorName = data.users.find((u) => u.id === creatorId)?.name || 'el usuario';
    const msg = `Asignar a ${creatorName} como creator de ${selected.size} equipo(s) y todas sus unidades hijas?${override ? '\n\nIncluye sobreescribir los que ya tienen owner.' : ''}`;
    if (!window.confirm(msg)) return;

    setSubmitting(true);
    try {
      const res = await api().post('/admin/backfill-creators', {
        equipment_ids: Array.from(selected),
        creator_user_id: creatorId,
        notify_email: notifyEmail.trim() || null,
        override_existing: override,
        cascade_to_units: true,
      });
      const orphans = res.data.orphan_count_for_creator || 0;
      const emailMsg = res.data.notify_email_sent ? ' + copia al correo extra' : '';
      toast.success(
        `${res.data.equipment_modified} equipo(s) y ${res.data.units_modified} unidad(es) asignadas. ` +
        `Notificación enviada al owner (in-app + email${emailMsg}).` +
        (orphans > 0 ? ` ⚠ ${orphans} huérfanas requieren su acción.` : '')
      );
      setSelected(new Set());
      setNotifyEmail('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo aplicar el backfill');
    } finally {
      setSubmitting(false);
    }
  };

  const SortHeader = ({ col, children }) => (
    <button
      onClick={() => toggleSort(col)}
      className="flex items-center gap-1 hover:text-foreground text-left"
      type="button"
    >
      {children}
      {sortBy === col && (sortDir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
    </button>
  );

  return (
    <Layout>
      <div className="container mx-auto p-4 lg:p-6 space-y-4" data-testid="owner-assignment-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <UserCheck className="h-6 w-6" />
              Asignación de Owner
            </h1>
            <p className="text-sm text-muted-foreground">
              Asigna o backfill el creador (owner) de equipos y sus unidades hijas.
              El owner recibe alertas automáticas cuando un item suyo se convierte en huérfano.
            </p>
          </div>
          <Button onClick={load} variant="outline" size="sm" disabled={loading} data-testid="owner-refresh">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Recargar
          </Button>
        </div>

        {/* Stats */}
        {data && (
          <div className="grid grid-cols-3 gap-3">
            <Card><CardContent className="pt-4">
              <p className="text-xs text-muted-foreground uppercase">Total Equipos</p>
              <p className="text-3xl font-bold" data-testid="stat-total">{data.summary.total_equipment}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4">
              <p className="text-xs text-muted-foreground uppercase">Con Owner</p>
              <p className="text-3xl font-bold text-emerald-500" data-testid="stat-with-owner">{data.summary.with_owner}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4">
              <p className="text-xs text-muted-foreground uppercase">Sin Owner</p>
              <p className="text-3xl font-bold text-rose-500" data-testid="stat-without-owner">{data.summary.without_owner}</p>
            </CardContent></Card>
          </div>
        )}

        {/* Filters + bulk-assign panel */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Asignación Masiva</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap gap-2 items-center">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  data-testid="owner-search"
                  className="pl-9"
                  placeholder="Buscar equipo u owner..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-[180px]" data-testid="owner-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="without_owner">Sin owner</SelectItem>
                  <SelectItem value="with_owner">Con owner</SelectItem>
                  <SelectItem value="all">Todos</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-wrap gap-2 items-center p-3 bg-muted/30 rounded-lg">
              <span className="text-sm text-muted-foreground">Asignar como owner:</span>
              <Select value={creatorId} onValueChange={setCreatorId}>
                <SelectTrigger className="w-[260px]" data-testid="owner-creator-select">
                  <SelectValue placeholder="Selecciona usuario..." />
                </SelectTrigger>
                <SelectContent>
                  {data?.users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name} <span className="text-xs text-muted-foreground">({u.role})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                value={notifyEmail}
                onChange={(e) => setNotifyEmail(e.target.value)}
                placeholder="Correo adicional opcional (ej: equipo@empresa.com)"
                className="w-[280px]"
                type="email"
                data-testid="owner-notify-email"
              />
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={override} onCheckedChange={setOverride} data-testid="owner-override" />
                Sobreescribir existentes
              </label>
              <Button
                onClick={apply}
                disabled={submitting || selected.size === 0 || !creatorId}
                className="ml-auto"
                data-testid="owner-apply-btn"
              >
                {submitting ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                Aplicar a {selected.size} seleccionado(s)
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground -mt-1 pl-1">
              Al aplicar, el owner recibirá una notificación en sistema y por correo con
              instrucciones sobre qué hacer, cómo y para qué. Si proporcionas un correo adicional,
              también recibirá una copia (sin notificación in-app).
            </p>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto max-h-[60vh]">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-muted/50 backdrop-blur z-10">
                  <tr>
                    <th className="text-left p-3 w-10">
                      <Checkbox
                        checked={rows.length > 0 && selected.size === rows.length}
                        onCheckedChange={toggleAll}
                        data-testid="owner-select-all"
                      />
                    </th>
                    <th className="text-left p-3 font-medium"><SortHeader col="name">Equipo</SortHeader></th>
                    <th className="text-left p-3 font-medium"><SortHeader col="units">Unidades</SortHeader></th>
                    <th className="text-left p-3 font-medium"><SortHeader col="owner">Owner actual</SortHeader></th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr><td colSpan={4} className="p-8 text-center text-muted-foreground">Cargando…</td></tr>
                  )}
                  {!loading && rows.length === 0 && (
                    <tr><td colSpan={4} className="p-8 text-center text-muted-foreground">
                      <AlertCircle className="h-5 w-5 mx-auto mb-2 opacity-50" />
                      Sin resultados
                    </td></tr>
                  )}
                  {!loading && rows.map((r) => (
                    <tr
                      key={r.equipment_id}
                      className={`border-t hover:bg-muted/30 cursor-pointer ${selected.has(r.equipment_id) ? 'bg-primary/5' : ''}`}
                      onClick={() => toggleRow(r.equipment_id)}
                      data-testid={`owner-row-${r.equipment_id}`}
                    >
                      <td className="p-3" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selected.has(r.equipment_id)}
                          onCheckedChange={() => toggleRow(r.equipment_id)}
                        />
                      </td>
                      <td className="p-3 font-medium">{r.equipment_name}</td>
                      <td className="p-3">
                        <Badge variant="outline">{r.unit_count}</Badge>
                        {r.units_with_distinct_creator > 1 && (
                          <Badge className="ml-1 bg-amber-600 text-xs">
                            {r.units_with_distinct_creator} creators distintos
                          </Badge>
                        )}
                      </td>
                      <td className="p-3">
                        {r.owner_id ? (
                          <span>{r.owner_name} <span className="text-xs text-muted-foreground">({r.owner_username})</span></span>
                        ) : (
                          <Badge className="bg-rose-600 text-xs">Sin owner</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
